import React from 'react';
import PageLayout from '@/components/layout/PageLayout';
import Table from '@/components/ui/tables/Table';
import { FilterDropdown } from '@/components/ui/filters/FilterDropdown';
import { ExportDropdown } from '@/components/ui/filters/ExportDropdown';
import { DateRangeFilter } from '@/components/ui/filters/DateRangeFilter';
import { IndicatorCard } from '@/components/ui/cards/IndicatorCard';
import { useMyIncidentsList } from '@/hooks/attendance/myAttendance/useMyIncidentsList';
import { formatDateToYYYYMMDD, getMinDateByMonths } from '@/utils';


export const MyIncidents: React.FC = () => {
  const {
    filters,
    setFilters,
    defaultFrom,
    defaultTo,
    filterDefinitions,
    formattedFilterValues,
    handleFilterChange,
    data,
    counters,
    onCounterSelect,
    totalRecords,
    isLoading,
    page,
    pageSize,
    setPage,
    setPageSize,
    onSearch,
    onSortChange,
    columns,
    actions,
    exportIncidents,
    isExporting,
  } = useMyIncidentsList();

  return (
    <PageLayout>
      <div className="flex flex-col gap-6" data-testid="my-incidents-page">
        {/* Indicator Cards */}
        <div className="flex flex-row gap-4 overflow-x-auto pb-4 px-2">
          {counters.map((counter) => (
            <IndicatorCard
              key={counter.key}
              label={counter.label}
              value={counter.value}
              variant={counter.variant}
              isSelected={counter.isSelected}
              onClick={() => onCounterSelect(counter.key)}
              data-testid={`indicator-${counter.key}`}
            />
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center justify-between">
          <div className="flex flex-wrap gap-3 items-center">
            <DateRangeFilter
              value={{ from: filters.from, to: filters.to }}
              defaultValue={{ from: defaultFrom, to: defaultTo }}
              onChange={(range) => {
                setFilters((previous) => ({ ...previous, from: range.from, to: range.to }));
              }}
              maxDate={formatDateToYYYYMMDD(new Date())}
              minDate={getMinDateByMonths()}
              closeOnComplete
            />
            <FilterDropdown
              filters={filterDefinitions}
              values={formattedFilterValues}
              onChangeFilter={handleFilterChange}
            />
          </div>
          <ExportDropdown
            onExport={exportIncidents}
            placement="right"
            isLoading={isExporting}
            availableFormats={['EXCEL']}
          />
        </div>

        {/* Table */}
        <Table
          columns={columns}
          data={data}
          totalRows={totalRecords}
          isLoading={isLoading}
          searchable={true}
          onSearch={onSearch}
          serverSideSort
          onSortChange={onSortChange}
          pagination={{
            page,
            pageSize,
            total: totalRecords,
            onChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          initialSort={{ key: 'incidentDate', direction: 'desc' }}
          actions={actions}
          data-testid="table-incidents"
        />
      </div>
    </PageLayout>
  );
};

export default MyIncidents;
